import { api, queryClient } from '@/lib/api-client.ts'
import { AxiosResponse } from 'axios'
import handleApiErr from '@/lib/helpers/handleApiGerError.ts'
import {
  NotificationsDataT,
  NotificationT,
} from '@/routes/projectStages/notifications/lib/types/notificationsTypes.ts'

/** Triage tabs — map to the per-stage notification collection sub-routes. */
export type NotificationTabT = 'all' | 'unread' | 'snoozed' | 'done'

/**
 * React Query key prefixes for the four notification list tabs. Kept together
 * so invalidation always covers every tab (a triage moves a row between them).
 */
export const NOTIFICATION_LIST_KEYS = [
  'getUnreadNotificationsByStageId',
  'getNotificationsByStageId',
  'getSnoozedNotificationsByStageId',
  'getDoneNotificationsByStageId',
] as const

export const notificationCountsKey = (stageId: string | number) =>
  ['notificationCounts', String(stageId)] as const

/**
 * Invalidate all four tab lists + the counts query for a stage. Same-client
 * consistency after a mutation comes from THIS call (the mutation response),
 * not from the Mercure echo — which only serves OTHER clients and may be
 * disconnected/recovering. Superset of the subscriber's onRecovery refetch.
 *
 * Also refreshes two issue caches that carry per-user notification state, so a
 * triage never leaves them stale (Mercure can't heal these — its read receipts
 * go to /project_stages/{sid}/issues/{id}/issue_events, a different topic than
 * the issue lists):
 *  - currStageIssueTypes: the sidebar unread badges (mail aggregate + per-type),
 *    seeded from `issueType.unreadNotificationsCount` (same 'inbox' predicate).
 *  - stageIssuesByIssueType: the issue-list "blue dot" (`hasUnreadNotification`
 *    on each issue). Without this, reading/snoozing a notification would leave a
 *    stale dot on a cached issue list (and vice-versa for a new arrival).
 * Both use the bare prefix (matches the adjacent currStageIssueTypes line and
 * the codebase's other issue-list invalidations) — a client only holds the
 * current stage's lists, and it sidesteps projectStageId string/number matching.
 */
export function invalidateNotificationTabs(stageId: string | number): void {
  const id = String(stageId)
  NOTIFICATION_LIST_KEYS.forEach(key => {
    void queryClient.invalidateQueries({ queryKey: [key, id] })
  })
  void queryClient.invalidateQueries({ queryKey: notificationCountsKey(id) })
  void queryClient.invalidateQueries({ queryKey: ['currStageIssueTypes'] })
  void queryClient.invalidateQueries({ queryKey: ['stageIssuesByIssueType'] })
}

export const getNotificationsByStageIdQuery = (stageId: string) => ({
  queryKey: ['getNotificationsByStageId', stageId],
  queryFn: ({ pageParam = 1 }) => getNotificationsByStageId(stageId, pageParam, 'all'),
  getNextPageParam: (lastPage: GetNotificationsReturnT) => lastPage?.nextPage,
  initialPageParam: 1,
  staleTime: 1000 * 30,
})

export const getUnreadNotificationsByStageIdQuery = (stageId: string) => ({
  queryKey: ['getUnreadNotificationsByStageId', stageId],
  queryFn: ({ pageParam = 1 }) => getNotificationsByStageId(stageId, pageParam, 'unread'),
  getNextPageParam: (lastPage: GetNotificationsReturnT) => lastPage?.nextPage,
  initialPageParam: 1,
  staleTime: 1000 * 30,
})

export const getSnoozedNotificationsByStageIdQuery = (stageId: string) => ({
  queryKey: ['getSnoozedNotificationsByStageId', stageId],
  queryFn: ({ pageParam = 1 }) => getNotificationsByStageId(stageId, pageParam, 'snoozed'),
  getNextPageParam: (lastPage: GetNotificationsReturnT) => lastPage?.nextPage,
  initialPageParam: 1,
  staleTime: 1000 * 30,
})

export const getDoneNotificationsByStageIdQuery = (stageId: string) => ({
  queryKey: ['getDoneNotificationsByStageId', stageId],
  queryFn: ({ pageParam = 1 }) => getNotificationsByStageId(stageId, pageParam, 'done'),
  getNextPageParam: (lastPage: GetNotificationsReturnT) => lastPage?.nextPage,
  initialPageParam: 1,
  staleTime: 1000 * 30,
})

export type GetNotificationsReturnT = {
  notifications: NotificationT[]
  allNotificationsCount: number
  nextPage: number | null
  currentPage: number
} | null

/** Build the collection URL for a triage tab ('all' = the base route). */
export function notificationsTabUrl(stageId: string, page: number, tab: NotificationTabT): string {
  const suffix = tab === 'all' ? '' : `/${tab}`
  return `/project_stages/${stageId}/notifications${suffix}?page=${page}`
}

async function getNotificationsByStageId(
  stageId: string,
  page: number,
  tab: NotificationTabT = 'all',
): Promise<GetNotificationsReturnT> {
  try {
    const response: AxiosResponse<NotificationsDataT> = await api.get(
      notificationsTabUrl(stageId, page, tab),
    )
    const notifications: NotificationT[] = response.data[`hydra:member`]
    const allNotificationsCount: number = response.data[`hydra:totalItems`] ?? 0
    const hasNextPage =
      page * notifications.length < allNotificationsCount && notifications.length > 0
    return {
      notifications,
      allNotificationsCount,
      nextPage: hasNextPage ? page + 1 : null,
      currentPage: page,
    }
  } catch (err) {
    handleApiErr(err)
    return null
  }
}

/**
 * Aggregate triage counts for a stage — the ONE source of truth for the tab
 * badges + sidebar badge + snooze-expiry timer. Backed by the plain-controller
 * endpoint (not an API Platform resource, so it is not in the generated types).
 */
export type NotificationCountsT = {
  inbox: number
  active: number
  snoozed: number
  done: number
  nextSnoozedUntil: string | null
}

async function getNotificationCounts(stageId: string): Promise<NotificationCountsT> {
  const response: AxiosResponse<NotificationCountsT> = await api.get(
    `/project_stages/${stageId}/notifications/counts`,
  )
  return response.data
}

export const notificationCountsQuery = (stageId: string) => ({
  queryKey: notificationCountsKey(stageId),
  queryFn: () => getNotificationCounts(stageId),
  staleTime: 1000 * 30,
})

/** Semantic triage actions — the server owns all timestamp side effects. */
export type TriageActionT = 'read' | 'unread' | 'done' | 'restore' | 'snooze' | 'unsnooze'

/**
 * POST the semantic triage command to `/notifications/{id}/triage`. Replaces
 * the legacy field-PATCH interpretation: the backend decides readAt/archivedAt
 * (repeat-Done is a no-op preserving the original timestamp; restore reinstates
 * archivedAt=null + an optional future snooze atomically).
 */
export async function triageNotification(
  notificationIri: string,
  action: TriageActionT,
  snoozedUntil?: Date,
) {
  return api.post(`${notificationIri}/triage`, {
    triageAction: action,
    ...(snoozedUntil ? { triageSnoozedUntil: snoozedUntil.toISOString() } : {}),
  })
}

// Thin, stable wrappers over the semantic op so existing call sites keep their
// intent-named API while all writes go through `/triage`.
export const markNotificationAsUnread = (iri: string) => triageNotification(iri, 'unread')
export const markNotificationAsRead = (iri: string) => triageNotification(iri, 'read')
export const snoozeNotification = (iri: string, until: Date) =>
  triageNotification(iri, 'snooze', until)
export const unsnoozeNotification = (iri: string) => triageNotification(iri, 'unsnooze')
export const markNotificationDone = (iri: string) => triageNotification(iri, 'done')
/** Undo Done — optionally reinstating a prior snooze atomically. */
export const unmarkNotificationDone = (iri: string, reinstateSnooze?: Date) =>
  triageNotification(iri, 'restore', reinstateSnooze)

/**
 * Mark every inbox notification in the stage as read (one atomic server-side
 * bulk update). Returns the affected count + fresh authoritative counts.
 */
export async function markAllNotificationsRead(
  stageId: string | number,
): Promise<{ updated: number; counts: NotificationCountsT }> {
  const response = await api.post(`/project_stages/${stageId}/notifications/mark_all_read`)
  return response.data
}

/** Per-id triage outcome from the batch endpoint. */
export type BatchTriageResultsT = { results: Record<string, 'ok' | 'failed'> }

/**
 * Apply one triage action to many notifications in a single request. Foreign or
 * nonexistent ids come back `failed` (indistinguishable — anti-enumeration).
 */
export async function triageNotificationsBatch(
  stageId: string | number,
  action: TriageActionT,
  ids: Array<string | number>,
  snoozedUntil?: Date,
): Promise<BatchTriageResultsT> {
  const response = await api.post(`/project_stages/${stageId}/notifications/triage_batch`, {
    action,
    ids: ids.map(id => Number(id)),
    ...(snoozedUntil ? { snoozedUntil: snoozedUntil.toISOString() } : {}),
  })
  return response.data
}

/**
 * Membership of a notification in each triage set at time `now`. NON-EXCLUSIVE:
 * `active` overlaps `inbox` and `snoozed`; a read, unsnoozed, active row is
 * active-only. This is the ONLY place membership logic lives — the tabs, the
 * count deltas, and the optimistic cache moves all derive from it. Mirrors the
 * backend `NotificationRepository::applyStateFilter` predicates exactly.
 */
export type NotificationMembershipT = {
  inbox: boolean
  active: boolean
  snoozed: boolean
  done: boolean
}

export function getMembership(n: NotificationT, now: number): NotificationMembershipT {
  const done = n?.archivedAt != null
  const active = !done && !n?.inAppSuppressed
  const snoozedUntilMs = n?.snoozedUntil ? new Date(n.snoozedUntil).getTime() : null
  const snoozed = active && snoozedUntilMs !== null && snoozedUntilMs > now
  const inbox = active && !snoozed && (n?.readAt == null || !!n?.markedAsUnread)
  return { inbox, active, snoozed, done }
}

/**
 * Apply a triage action to a CLONE of the row (never mutates the input),
 * reproducing the backend `NotificationTriageApplier` semantics so the
 * optimistic membership diff matches what the server will persist.
 */
export function applyTriageToClone(
  n: NotificationT,
  action: TriageActionT,
  snoozedUntil: Date | undefined,
  now: Date,
): NotificationT {
  const next = { ...n }
  const nowIso = now.toISOString()
  switch (action) {
    case 'read':
      next.readAt = next.readAt ?? nowIso // never overwrite
      next.markedAsUnread = false
      break
    case 'unread':
      next.markedAsUnread = true
      break
    case 'done':
      // Idempotent: already-archived is untouched (preserves original stamp).
      if (next.archivedAt == null) {
        next.archivedAt = nowIso
        next.snoozedUntil = null
      }
      break
    case 'restore':
      next.archivedAt = null
      if (snoozedUntil) next.snoozedUntil = snoozedUntil.toISOString()
      break
    case 'snooze':
      if (snoozedUntil) next.snoozedUntil = snoozedUntil.toISOString()
      break
    case 'unsnooze':
      next.snoozedUntil = null
      break
  }
  return next
}

/** Per-set count delta (−1 | 0 | +1) from a before/after membership diff. */
export type NotificationCountDeltaT = {
  inbox: number
  active: number
  snoozed: number
  done: number
}

export function deriveMembershipDeltas(
  before: NotificationMembershipT,
  after: NotificationMembershipT,
): NotificationCountDeltaT {
  const d = (b: boolean, a: boolean) => (a ? 1 : 0) - (b ? 1 : 0)
  return {
    inbox: d(before.inbox, after.inbox),
    active: d(before.active, after.active),
    snoozed: d(before.snoozed, after.snoozed),
    done: d(before.done, after.done),
  }
}
